import { ProductType, SelectedOptions } from '../reducers/product.reducer';
import { getProductQuantity } from './product-utils';
import type { CartItem } from '@app/checkout/modules/checkout-shared/reducers/checkout.reducer';

/**
 * Check that given product has enough stock for the selected quantity.
 * @param product Product received from database
 * @param selectedOptions Selected options from product editor
 * @param cartItems Items currently in shopping cart
 * @param [changedItem] Cart item being changed. This item won't be included in the
 *   "quantity in cart" calculations.
 * @returns The amount of available stock for the selected quantity (negative if not enough stock).
 */
export function getStockAmount(
	product: api.ProductDto,
	selectedOptions: SelectedOptions,
	cartItems?: CartItem[],
	changedItem?: CartItem,
): number {
	// Validate parameters.
	if (!product?.type || !selectedOptions) return -Infinity;
	if (product.type !== ProductType.Stock) return Infinity;
	// Calculate the quantity wanted & available.
	const quantityWanted: number = getProductQuantity({ options: selectedOptions }, 0);
	const quantityAvailable = product?.stock_level ?? 0;
	// Calculate the amount of stock available considering all the items in the cart.
	let quantityInCart = 0;
	for (const item of cartItems ?? []) {
		// Don't include the item being changed to the "quantity in cart" calculation if any.
		// Also skip the item if it's not the same product.
		if (changedItem?.id === item.id || product.id !== item?.product?.id) continue;
		quantityInCart += getProductQuantity(item, 0);
	}
	const stockDelta = quantityAvailable - quantityInCart - quantityWanted;

	return stockDelta;
}

/**
 * Check that given product has enough stock for the selected quantity.
 * Products that aren't stock products will always receive a positive status.
 * @param product Product received from database
 * @param selectedOptions Selected options from product editor
 * @param cartItems Items currently in shopping cart
 * @param [changedItem] Cart item being changed. This item won't be included in the
 *   "quantity in cart" calculations.
 * @returns Whether the product has enough stock for the selected quantity or not.
 */
export function getStockStatus(
	product: api.ProductDto,
	selectedOptions: SelectedOptions,
	cartItems?: CartItem[],
	changedItem?: CartItem,
): boolean {
	return getStockAmount(product, selectedOptions, cartItems, changedItem) >= 0;
}

/**
 * Check if product is out of stock and there's instances of the product in shopping cart.
 * Used for showing a specialized error message.
 * @param stockStatus Result of getStockStatus()
 * @param product Product received from database
 * @param cartItems Items currently in shopping cart
 * @returns Stock status
 */
export function isTotalQuantityOutOfStock(
	stockStatus: boolean,
	product: api.ProductDto,
	cartItems: api.CartItemWithProductDto[],
): boolean {
	if (stockStatus) return false;
	const itemsInCart = (cartItems || []).filter((cartItem) => product.id === cartItem?.product?.id);
	return itemsInCart.length > 0;
}
